// useBlePrintingRollout — gate de la feature de impresión Bluetooth (BLE_FEATURE_KEY),
// instancia del contrato `FeatureGate` (ver useFeatureGate.ts). TODA la lógica de
// elegibilidad de BT vive AQUÍ, una sola vez; navbar/Ajustes/PrintingSection solo consumen:
//
//   - `offered`: el comercio TIENE la funcionalidad (treatment o GA con flag de build) →
//     se muestra el bloque BT en Ajustes. Flag OFF (prod hoy) → hook del framework inerte,
//     feature dark. La condición de runtime "este navegador soporta BT" se evalúa en el
//     render de la sección (decide entre el bloque y el aviso explicativo, matiz de producto).
//   - `inviteEligible`: control invitable + este dispositivo es COMPATIBLE → etiquetas
//     "Nuevo". La compatibilidad es per-dispositivo (Web Bluetooth + contexto seguro) aunque
//     la asignación es per-comercio: NO se invita donde la funcionalidad no puede usarse.
//   - `showCoachmark`: además el backend autoriza invitar AHORA (invite_surface con la
//     cadencia/cooldown/snooze server-side).
//
// NOTA producto: el gate gobierna el OFRECIMIENTO (UI de Ajustes). La impresión en runtime de
// impresoras YA emparejadas no se apaga por variante: nunca rompemos la operación del comercio
// a media jornada por un cambio de bucket.

import { makeFeatureGateHook, type FeatureGate } from '@hooks/useFeatureGate';
import { BLE_FEATURE_KEY } from '@utils/featureRollout/constants';
import { isWebBleSupported } from '@utils/print/webble/bleTransport';

// Compatibilidad del DISPOSITIVO actual: navegador con Web Bluetooth + contexto seguro (https/
// localhost). Es la condición mínima para que el flujo BT pueda funcionar aquí.
export const isBleDeviceCompatible = (): boolean =>
  isWebBleSupported() && typeof window !== 'undefined' && Boolean(window.isSecureContext);

export const BLE_INVITE_TRIGGER = 'ble_coachmark';

const useBleGate = makeFeatureGateHook({
  featureKey: BLE_FEATURE_KEY,
  inviteTrigger: BLE_INVITE_TRIGGER,
  useInvitable: isBleDeviceCompatible,
});

// Superficie pública con el alias histórico `showCoachmark` (= showInvite del contrato).
export interface BlePrintingRollout extends FeatureGate {
  /** inviteEligible + el backend autoriza invitar ahora (alias de showInvite). */
  showCoachmark: boolean;
}

export const useBlePrintingRollout = (): BlePrintingRollout => {
  const gate = useBleGate();
  return { ...gate, showCoachmark: gate.showInvite };
};
